import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DoorClosed, ScrollText, ShieldCheck, Users } from 'lucide-react';
import { display } from '../asistencia/ui';
import PuertasVista from './PuertasVista';
import RegistrosVista from './RegistrosVista';
import PersonasVista from './PersonasVista';
import GruposVista from './GruposVista';
import { useEnVivo } from './useEnVivo';
import './puertas.css';

const VISTAS = [
    { id: 'puertas', label: 'Puertas', icono: DoorClosed, componente: PuertasVista },
    { id: 'registros', label: 'Últimos registros', icono: ScrollText, componente: RegistrosVista },
    { id: 'personas', label: 'Personas', icono: Users, componente: PersonasVista },
    { id: 'grupos', label: 'Grupos de acceso', icono: ShieldCheck, componente: GruposVista },
];

// Arriba de la navegación: si el controlador responde
const EstadoControlador = ({ estado, error, onIr }) => {
    if (!estado && !error) return <div className="h-[52px]" />;
    const puertas = estado?.puertas || [];
    const enLinea = !error && puertas.some((p) => p.en_linea);
    return (
        <button
            type="button" onClick={onIr}
            className="w-full text-left rounded-xl px-3 py-2.5 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
        >
            <span className="flex items-center gap-2 text-sm text-slate-700">
                <span className={`w-2 h-2 rounded-full ${enLinea ? 'bg-cyan-400' : 'bg-rose-500'}`} aria-hidden />
                {error ? 'Sin GeoVision' : enLinea ? 'ALPHA en línea' : 'ALPHA sin conexión'}
            </span>
            {!error && <span className="block text-xs text-slate-500 mt-1 pl-4">Controlador de las puertas</span>}
        </button>
    );
};

const PuertasView = () => {
    const [params, setParams] = useSearchParams();
    const actual = VISTAS.find((v) => v.id === params.get('vista')) || VISTAS[0];
    const irA = useCallback((id, extra = {}) => setParams(id === 'puertas' ? {} : { vista: id, ...extra }), [setParams]);
    const vivo = useEnVivo();

    const Vista = actual.componente;
    return (
        <div className="bg-[#F6F7F9] flex -m-4 md:-m-8 w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] h-[calc(100%+2rem)] md:h-[calc(100%+4rem)] overflow-hidden">
            <aside className="hidden lg:flex w-60 shrink-0 flex-col bg-white border-r border-slate-200/80">
                <div className="px-5 pt-6 pb-3">
                    <p className={`${display} text-xl font-bold text-sura-blue`}>Puertas</p>
                </div>
                <div className="px-2">
                    <EstadoControlador estado={vivo.estado} error={vivo.error} onIr={() => irA('puertas')} />
                </div>
                <nav className="flex-1 overflow-y-auto px-3 pt-4 pb-6" aria-label="Puertas">
                    <ul className="space-y-0.5">
                        {VISTAS.map((v) => {
                            const activa = v.id === actual.id;
                            const Icono = v.icono;
                            return (
                                <li key={v.id}>
                                    <button
                                        type="button" onClick={() => irA(v.id)} aria-current={activa ? 'page' : undefined}
                                        className={`relative w-full flex items-center gap-2.5 min-h-9 py-2 px-3 rounded-lg text-sm text-left whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 ${
                                            activa ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                        }`}
                                    >
                                        {activa && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-sura-blue" />}
                                        <Icono size={16} className={`shrink-0 ${activa ? 'text-sura-blue' : 'text-slate-400'}`} />
                                        {v.label}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </nav>
            </aside>

            <main className="flex-1 min-w-0 overflow-y-auto scroll-fino">
                {/* En pantallas chicas la navegación va arriba */}
                <div className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200/80 px-4 py-3">
                    <label className="sr-only" htmlFor="puertas-vista">Sección</label>
                    <select
                        id="puertas-vista" value={actual.id} onChange={(e) => irA(e.target.value)}
                        className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-800"
                    >
                        {VISTAS.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                    </select>
                </div>
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-8">
                    <Vista key={actual.id} vivo={vivo} irA={irA} filtroInicial={{ elemento: params.get('elemento') || '' }} />
                </div>
            </main>
        </div>
    );
};

export default PuertasView;
